import React, { useState, useEffect, useCallback, useRef } from 'react';
import { auth, db } from './firebase';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut
} from 'firebase/auth';
import {
  doc,
  getDoc,
  deleteDoc,
  updateDoc,
  arrayUnion,
  setDoc
} from 'firebase/firestore';

// Components
import Loading from './components/common/Loading';
import LoginForm from './components/auth/LoginForm';
import Header from './components/layout/Header';
import NoGroupsView from './components/layout/NoGroupsView';
import EditProfileModal from './components/common/EditProfileModal';
import BandsintownImport from './components/BandsintownImport';
import OfflineIndicator from './components/common/OfflineIndicator';
import NotificationSettingsModal from './components/common/NotificationSettingsModal';

// Page imports
import Dashboard from './components/pages/Dashboard';
import Calendar from './components/pages/Calendar';
import Contacts from './components/pages/Contacts';
import Documents from './components/pages/Documents';
import Summary from './components/pages/Summary';
import Timeline from './components/pages/Timeline';
import Expenses from './components/pages/Expenses';
import FeedbackAdmin from './components/pages/FeedbackAdmin';
import GroupSettings from './components/pages/GroupSettings';
import Inventory from './components/pages/Inventory';
import Polls from './components/pages/Polls';

// Onboarding components
import GuidedTour from './components/onboarding/GuidedTour';
import WelcomeModal from './components/onboarding/WelcomeModal';
import DemoModeBanner from './components/onboarding/DemoModeBanner';
import CreateBandModal from './components/onboarding/CreateBandModal';
import { generateDemoData } from './data/demoData';

// Common components
import toast, { Toaster } from 'react-hot-toast';
import EmailUpdatesBanner from './components/common/EmailUpdatesBanner';
import PWAInstallPrompt from './components/common/PWAInstallPrompt';
import FeedbackModal from './components/common/FeedbackModal';
import BottomNav from './components/common/BottomNav';
import SplashScreen from './components/common/SplashScreen';

// Group components
import JoinGroup, { JoinGroupManual } from './components/pages/JoinGroup';

// Contexts
import { GroupProvider, useGroup } from './contexts/GroupContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';

// Hooks
import {
  useFirebaseOptimized as useFirebase,
  useMigrateData,
  usePresence,
  usePermissions
} from './hooks/useFirebaseOptimized';
import { useAppData } from './hooks/useAppData';
import { useExport } from './hooks/useExport';
import { useSwipeGesture } from './hooks/useSwipeGesture';

// Utils
import { calculateSummary } from './utils/calculations';
import { initAnalytics, trackOnboarding } from './utils/analytics';

// =====================================================
// ADMIN EMAIL - Change this to your email to access feedback admin
// =====================================================
const ADMIN_EMAIL = 'medeirossethcode@gmail.com';

// =====================================================
// MAIN APP COMPONENT
// =====================================================
function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authMode, setAuthMode] = useState('login');
  const [pendingInvite, setPendingInvite] = useState(null);
  const [showSplash, setShowSplash] = useState(true);
  
  // Check URL for invite codes on mount
  useEffect(() => {
    const path = window.location.pathname;
    const match = path.match(/\/join\/([A-Z0-9]+)/i);
    if (match) {
      setPendingInvite(match[1].toUpperCase());
    }
  }, []);

  // Auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Auth functions
  const handleLogin = async (email, password) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      throw error;
    }
  };

  const handleSignup = async (email, password, displayName) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const userDocRef = doc(db, 'users', userCredential.user.uid);
      await setDoc(userDocRef, {
        email: email,
        displayName: displayName || email.split('@')[0],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      throw error;
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  if (showSplash) {
    return <SplashScreen onComplete={() => setShowSplash(false)} minimumDuration={2000} />;
  }

  if (authLoading) return <Loading />;

  if (!user) {
    return (
      <LoginForm
        onLogin={handleLogin}
        onSignup={handleSignup}
        authMode={authMode}
        setAuthMode={setAuthMode}
      />
    );
  }

  return (
    <GroupProvider user={user}>
      <AuthenticatedApp
        user={user}
        onLogout={handleLogout}
        pendingInvite={pendingInvite}
        onClearInvite={() => {
          setPendingInvite(null);
          window.history.replaceState({}, '', '/');
        }}
      />
    </GroupProvider>
  );
}

// =====================================================
// AUTHENTICATED APP COMPONENT
// =====================================================
function AuthenticatedApp({ user, onLogout, pendingInvite, onClearInvite }) {
  // Group context
  const { 
    currentGroup, 
    userGroups, 
    loading: groupLoading, 
    createGroup, 
    joinGroup, 
    updateMemberName, 
    isAdmin 
  } = useGroup();
  
  const { migrateUserDataToGroup, checkForLegacyData } = useMigrateData(user);
  const { onlineMembers } = usePresence(user, currentGroup);
  const { permissions } = usePermissions(user, currentGroup);

  // =====================================================
  // UI STATE
  // =====================================================
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [showJoinGroup, setShowJoinGroup] = useState(false);
  const [showGuidedTour, setShowGuidedTour] = useState(false);
  const [emailInvite, setEmailInvite] = useState(null);
  const [emailInviteChecked, setEmailInviteChecked] = useState(false);
  const [bandsintownModalOpen, setBandsintownModalOpen] = useState(false);
  const [editProfileModalOpen, setEditProfileModalOpen] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [userCurrency, setUserCurrency] = useState('USD');
  const [inventoryMigrated, setInventoryMigrated] = useState(true); // default true — always use subcollection
  const [pendingShowToEdit, setPendingShowToEdit] = useState(null);
  const [notificationSettingsOpen, setNotificationSettingsOpen] = useState(false);

  // =====================================================
  // ONBOARDING STATE
  // =====================================================
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showCreateBandModal, setShowCreateBandModal] = useState(false);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [highlightUpcomingShows, setHighlightUpcomingShows] = useState(false);
  const [isCreatingBand, setIsCreatingBand] = useState(false);
  const [onboardingComplete, setOnboardingComplete] = useState(() => {
    return localStorage.getItem('boply_onboarding_completed') === 'true';
  });

  // UI preferences
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const [dashboardEditing, setDashboardEditing] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);

  // Ref to track previous shows count for detecting deletes
  const prevShowsCountRef = useRef(0);

  // =====================================================
  // DATA HOOKS
  // =====================================================
  const appData = useAppData(user, currentGroup, isDemoMode, inventoryMigrated, setInventoryMigrated);
  
  const {
    shows,
    setShows,
    generalExpenses,
    inventory,
    documents,
    contacts,
    polls,
    salesLog,
    setSalesLog,
    dataLoaded,
    setDataLoaded,
    hasLocalChanges,
    setHasLocalChanges,
    handleDataChange,
    resetAllData,
    // Show handlers
    addShow,
    updateShow,
    deleteShow,
    toggleShow,
    // Expense handlers
    addGeneralExpense,
    updateGeneralExpense,
    deleteGeneralExpense,
    // Inventory handlers
    addInventoryItem,
    updateInventoryItem,
    deleteInventoryItem,
    handleRecordMerchSale,
    // Contact handlers
    addContact,
    updateContact,
    deleteContact,
    // Poll handlers
    handleAddPoll,
    handleVotePoll,
    handleClosePoll,
    handleDeletePoll,
    // Document handlers
    handleAddDocument,
    handleUpdateDocument,
    handleDeleteDocument,
    // Sales log handlers
    handleAddSaleLog,
    // Helper functions
    getAllShowsData,
    getUpcomingShows,
    getTotalInventoryValue,
    getUnvotedPollsCount,
  } = appData;

  // Export functions
  const {
    handleExportShows,
    handleExportExpenses,
    handleExportInventory,
    handleExportAll,
  } = useExport(shows, generalExpenses, inventory);

  // Firebase sync
  const {
    saveToFirebase,
    flushPendingSave,
    loading: saveLoading,
    connected,
    syncStatus
  } = useFirebase(user, currentGroup, handleDataChange);

  // =====================================================
  // EFFECTS
  // =====================================================
  
  // Initialize analytics
  useEffect(() => {
    initAnalytics();
  }, []);

  // Dark mode effect
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  // Check for pending email invites
  useEffect(() => {
    const checkEmailInvites = async () => {
      if (!user?.email) {
        setEmailInviteChecked(true);
        return;
      }

      try {
        const inviteDoc = await getDoc(doc(db, 'emailInvites', user.email.toLowerCase()));
        if (inviteDoc.exists()) {
          const inviteData = inviteDoc.data();
          const isAlreadyMember = userGroups.some(g => g.id === inviteData.groupId);
          if (!isAlreadyMember) {
            setEmailInvite(inviteData);
          } else {
            await deleteDoc(doc(db, 'emailInvites', user.email.toLowerCase()));
          }
        }
      } catch (err) {
        console.error('Error checking email invites:', err);
      }
      setEmailInviteChecked(true);
    };

    if (!groupLoading) {
      checkEmailInvites();
    }
  }, [user?.email, groupLoading, userGroups]);

  // =====================================================
  // CHECK FOR NEW USERS - SHOW WELCOME MODAL
  // =====================================================
  useEffect(() => {
    // Skip if onboarding already complete
    if (onboardingComplete) return;
    
    // Skip if currently creating a band
    if (isCreatingBand) return;
    
    // Wait for loading
    if (!user?.uid || groupLoading || !emailInviteChecked) return;
    
    // Skip if has pending invites
    if (emailInvite || pendingInvite) return;
    
    // If user already has groups, mark complete and skip
    if (userGroups && userGroups.length > 0) {
      localStorage.setItem('boply_onboarding_completed', 'true');
      setOnboardingComplete(true);
      return;
    }
    
    // Check localStorage
    const hasSeenWelcome = localStorage.getItem('boply_welcome_seen');
    if (hasSeenWelcome) return;
    
    // Show welcome modal for new users
    setShowWelcomeModal(true);
  }, [user?.uid, groupLoading, emailInviteChecked, emailInvite, pendingInvite, userGroups, isCreatingBand, onboardingComplete]);

  // =====================================================
  // RESET CREATING STATE WHEN GROUP IS READY
  // =====================================================
  useEffect(() => {
    if (isCreatingBand && currentGroup && userGroups.length > 0) {
      console.log('Group loaded, resetting isCreatingBand');
      setIsCreatingBand(false);
      setShowCreateBandModal(false);
      localStorage.setItem('boply_onboarding_completed', 'true');
      setOnboardingComplete(true);
    }
  }, [isCreatingBand, currentGroup, userGroups]);

  // Handle pending invite
  useEffect(() => {
    if (pendingInvite && !groupLoading) {
      setShowJoinGroup(true);
    }
  }, [pendingInvite, groupLoading]);

  // Check for legacy data migration
  useEffect(() => {
    const checkMigration = async () => {
      if (currentGroup && userGroups.length === 1 && !dataLoaded) {
        const hasLegacy = await checkForLegacyData();
        if (hasLegacy) {
          const shouldMigrate = window.confirm(
            'We found existing data in your account. Would you like to import it into this band?'
          );
          if (shouldMigrate) {
            await migrateUserDataToGroup(currentGroup.id);
            toast.success('Data imported successfully!');
          }
        }
      }
    };
    checkMigration();
  }, [currentGroup?.id, userGroups.length, dataLoaded, checkForLegacyData, migrateUserDataToGroup]);

  // Auto-save when data changes (with immediate save for deletes)
  useEffect(() => {
    if (isDemoMode) return;
    
    if (currentGroup && hasLocalChanges && dataLoaded) {
      const dataToSave = inventoryMigrated
        ? { shows, generalExpenses, documents, contacts, polls }
        : { shows, generalExpenses, inventory, documents, contacts, polls };

      // Count current shows
      const currentShowsCount = Object.values(shows || {}).flat().length;
      
      // Check if this is a delete operation (fewer shows than before)
      const isDelete = currentShowsCount < prevShowsCountRef.current;
      
      // Save immediately for deletes, debounced for other changes
      saveToFirebase(dataToSave, isDelete);
      setHasLocalChanges(false);
      
      // Update the previous count
      prevShowsCountRef.current = currentShowsCount;
    }
  }, [shows, generalExpenses, inventory, documents, contacts, polls, currentGroup, hasLocalChanges, dataLoaded, saveToFirebase, inventoryMigrated, isDemoMode, setHasLocalChanges]);

  // Initialize prevShowsCountRef when data first loads
  useEffect(() => {
    if (dataLoaded && shows) {
      prevShowsCountRef.current = Object.values(shows || {}).flat().length;
    }
  }, [dataLoaded, shows]);

  // =====================================================
  // DEMO MODE HANDLERS
  // =====================================================
  const handleStartDemo = () => {
    console.log('handleStartDemo called');
    
    // Mark welcome as seen
    localStorage.setItem('boply_welcome_seen', 'true');
    setShowWelcomeModal(false);

    const demo = generateDemoData();
    setIsDemoMode(true);
    
    appData.setDemoData(demo);

    trackOnboarding?.demoStarted?.();
    toast.success('Welcome! Explore the demo to see how Boply works.', {
      duration: 4000,
      icon: '🎸'
    });
  };

  const handleStartFresh = () => {
    console.log('handleStartFresh called');
    
    // Prevent re-entry if already creating
    if (isCreatingBand) {
      console.log('Already creating band, skipping...');
      return;
    }
    
    // Mark welcome as seen IMMEDIATELY
    localStorage.setItem('boply_welcome_seen', 'true');
    setShowWelcomeModal(false);
    
    // Show the Create Band modal
    setShowCreateBandModal(true);
    
    trackOnboarding?.startFreshChosen?.();
  };

  const handleExitDemo = () => {
    console.log('handleExitDemo called');
    setShowCreateBandModal(true);
  };

  // =====================================================
  // HANDLE BAND CREATED - FIXED
  // =====================================================
  const handleBandCreated = async (data) => {
    console.log('handleBandCreated called with:', data);
    
    // CRITICAL: Prevent double-execution
    if (isCreatingBand) {
      console.log('Already creating band, preventing double execution');
      return;
    }
    
    setIsCreatingBand(true);
    setShowCreateBandModal(false);
    setIsDemoMode(false);

    // Set these immediately so the no-groups view doesn't re-show during async gap
    localStorage.setItem('boply_welcome_seen', 'true');
    localStorage.setItem('boply_onboarding_completed', 'true');
    setOnboardingComplete(true);

    // Reset any demo data
    resetAllData();

    try {
      // Create the group
      console.log('Creating group:', data.bandName);
      await createGroup(data.bandName);
      console.log('Group created successfully');

      // Reset creating flag immediately after group is written to Firestore.
      // Don't wait for the useEffect — that has a race condition where
      // currentGroup may not be in context yet when this line runs.
      setIsCreatingBand(false);
      setShowCreateBandModal(false);

      trackOnboarding?.bandCreated?.(data.bandName, true);

      // Handle first show if provided
      if (data.firstShow) {
        const showId = Date.now();
        const dateKey = data.firstShow.date;
        
        console.log('Adding first show:', dateKey);
        
        // Wait a bit for group to be ready, then add show
        setTimeout(() => {
          const newShow = {
            id: showId,
            venue: data.firstShow.venue || '',
            city: data.firstShow.city || '',
            guarantee: 0,
            date: dateKey,
            eventType: 'show',
            status: data.firstShow.status || 'confirmed',
            isFirstShow: true,
            completed: data.firstShow.status === 'completed',
          };
          
          setShows(prev => {
            // Make sure we're not adding to demo data
            const isClean = !prev || Object.keys(prev).length === 0 || 
              !Object.values(prev).flat().some(s => s.id?.toString().startsWith('demo-'));
            
            if (isClean) {
              return { ...prev, [dateKey]: [...(prev[dateKey] || []), newShow] };
            }
            return prev;
          });
          
          setHasLocalChanges(true);
          trackOnboarding?.firstShowAdded?.(data.firstShow);
        }, 2000);

        trackOnboarding?.completed?.(true);
        setCurrentPage('dashboard');
        setHighlightUpcomingShows(true);
        toast.success(`Your first show is on the calendar! 🎉`, { duration: 5000 });
        
      } else {
        trackOnboarding?.completed?.(false);
        toast.success(`${data.bandName} is ready to rock! 🎸`);
        setCurrentPage('dashboard');
      }

      // Show guided tour after a delay
      setTimeout(() => setShowGuidedTour(true), 3000);
      
    } catch (error) {
      console.error('Error creating band:', error);
      toast.error('Failed to create band. Please try again.');
      
      // Reset state on error so user can retry
      setIsCreatingBand(false);
      setOnboardingComplete(false);
      localStorage.removeItem('boply_onboarding_completed');
      setShowCreateBandModal(true);
      return;
    }
    
    // NOTE: We intentionally do NOT set isCreatingBand to false here
    // The useEffect above will reset it when the group loads in context
  };

  // =====================================================
  // HELPER FUNCTIONS
  // =====================================================

  // Swipe right from edge → go to dashboard
  const { swipeStyle, ...swipeHandlers } = useSwipeGesture({
    onSwipeRight: () => {
      if (currentPage !== 'dashboard') {
        setCurrentPage('dashboard');
      }
    },
    edgeOnly: true,
    threshold: 75,
    enableVisualFeedback: false,
  });

  const toggleDarkMode = () => setDarkMode(prev => !prev);

  const handleCurrencyChange = async (newCurrency) => {
    setUserCurrency(newCurrency);
    if (!user?.uid) return;

    try {
      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(userDocRef, {
        currency: newCurrency,
        updatedAt: new Date().toISOString()
      }, { merge: true });
    } catch (error) {
      console.error('Error saving currency preference:', error);
    }
  };

  const getGroupMembers = () => {
    if (isDemoMode) {
      return [
        { id: user.uid, odatauserId: user.uid, name: 'You', role: 'Admin', isCurrentUser: true },
        { id: 'demo-member-2', odatauserId: 'demo-member-2', name: 'Alex', role: 'Member' },
        { id: 'demo-member-3', odatauserId: 'demo-member-3', name: 'Sam', role: 'Member' },
      ];
    }
    if (!currentGroup?.members) return [];
    return Object.entries(currentGroup.members)
      .filter(([key, value]) => value !== null && value !== undefined)
      .map(([key, value]) => ({ ...value, odatauserId: key, odataid: key }));
  };

  const handleLogout = async () => {
    await flushPendingSave();
    resetAllData();
    setCurrentPage('dashboard');
    await onLogout();
  };

  const handleBandsintownImport = (showsToAdd) => {
    showsToAdd.forEach(({ dateKey, show }) => {
      addShow(dateKey, show);
    });
    toast.success(`Imported ${showsToAdd.length} show${showsToAdd.length !== 1 ? 's' : ''} from Bandsintown!`);
  };

  // =====================================================
  // LOADING & SPECIAL VIEWS
  // =====================================================
  // Show loading while groups fetch OR while band is being created
  if (groupLoading || isCreatingBand) {
    return <Loading />;
  }

  // Join group via invite link
  if (showJoinGroup && pendingInvite) {
    return (
      <JoinGroup
        inviteCode={pendingInvite}
        user={user}
        onSuccess={() => {
          setShowJoinGroup(false);
          onClearInvite();
          setCurrentPage('dashboard');
          toast.success('Successfully joined the band!');
        }}
        onCancel={() => {
          setShowJoinGroup(false);
          onClearInvite();
        }}
      />
    );
  }

  // Email invite modal
  if (emailInvite) {
    const handleAcceptEmailInvite = async () => {
      try {
        const groupRef = doc(db, 'groups', emailInvite.groupId);
        const groupDocSnap = await getDoc(groupRef);

        if (!groupDocSnap.exists()) {
          toast.error('This band no longer exists');
          setEmailInvite(null);
          return;
        }

        const newMember = {
          odatauserId: user.uid,
          odataid: user.uid,
          email: user.email,
          name: user.displayName || user.email?.split('@')[0],
          role: emailInvite.role || 'member',
          joinedAt: new Date().toISOString()
        };

        await updateDoc(groupRef, {
          [`members.${user.uid}`]: newMember,
          memberIds: arrayUnion(user.uid)   // keep flat array in sync
        });

        await deleteDoc(doc(db, 'emailInvites', user.email.toLowerCase()));
        setEmailInvite(null);
        toast.success(`Welcome to ${emailInvite.groupName}! 🎉`);
        window.location.reload();
      } catch (err) {
        console.error('Error accepting email invite:', err);
        toast.error('Failed to join band. Please try again.');
      }
    };

    const handleDeclineEmailInvite = async () => {
      try {
        await deleteDoc(doc(db, 'emailInvites', user.email.toLowerCase()));
      } catch (err) {
        console.error('Error declining invite:', err);
      }
      setEmailInvite(null);
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 w-full max-w-md text-center">
          <div className="text-6xl mb-4">🎸</div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">You're Invited!</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            <span className="font-semibold text-purple-600 dark:text-purple-400">
              {emailInvite.invitedByEmail || 'Someone'}
            </span>{' '}invited you to join
          </p>
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4 mb-6">
            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
              {emailInvite.groupName}
            </p>
          </div>
          <div className="space-y-3">
            <button
              onClick={handleAcceptEmailInvite}
              className="w-full py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-colors"
            >
              Join Band
            </button>
            <button
              onClick={handleDeclineEmailInvite}
              className="w-full py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Not Now
            </button>
          </div>
        </div>
      </div>
    );
  }

  // =====================================================
  // NO GROUPS VIEW
  // =====================================================
  if (
    !currentGroup && 
    userGroups.length === 0 && 
    emailInviteChecked && 
    !emailInvite && 
    !isDemoMode &&
    !isCreatingBand &&
    !onboardingComplete
  ) {
    // Auto-show welcome modal only for brand new users
    if (!showWelcomeModal && !showCreateBandModal && !localStorage.getItem('boply_welcome_seen')) {
      setShowWelcomeModal(true);
    }

    return (
      <>
        {showWelcomeModal && (
          <WelcomeModal
            onStartDemo={handleStartDemo}
            onStartFresh={handleStartFresh}
          />
        )}

        {showCreateBandModal && (
          <CreateBandModal
            isOpen={showCreateBandModal}
            onComplete={handleBandCreated}
            onBack={() => {
              setShowCreateBandModal(false);
              setShowWelcomeModal(true);
            }}
          />
        )}

        {!showWelcomeModal && !showCreateBandModal && (
          <NoGroupsView
            user={user}
            onLogout={handleLogout}
            onCreateBand={() => setShowCreateBandModal(true)}
            joinGroup={joinGroup}
          />
        )}
      </>
    );
  }

  // =====================================================
  // MAIN RENDER
  // =====================================================
  const summary = calculateSummary(shows, generalExpenses);

  return (
    <SubscriptionProvider userId={user.uid} bandId={currentGroup?.id}>
      <>
        <Header
          user={user}
          isDemoMode={isDemoMode}
          currentPage={currentPage}
          darkMode={darkMode}
          showSettingsMenu={showSettingsMenu}
          setShowSettingsMenu={setShowSettingsMenu}
          syncStatus={syncStatus}
          connected={connected}
          onlineMembers={onlineMembers}
          setCurrentPage={setCurrentPage}
          setEditProfileModalOpen={setEditProfileModalOpen}
          setDashboardEditing={setDashboardEditing}
          setBandsintownModalOpen={setBandsintownModalOpen}
          setFeedbackModalOpen={setFeedbackModalOpen}
          setShowGuidedTour={setShowGuidedTour}
          setNotificationSettingsOpen={setNotificationSettingsOpen} 
          toggleDarkMode={toggleDarkMode}
          handleLogout={handleLogout}
          handleExitDemo={handleExitDemo}
        />

        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 3000,
            success: { duration: 3000 },
            error: { duration: 4000 },
          }}
        />

        {/* Demo Mode Banner */}
        {isDemoMode && <DemoModeBanner onCreateBand={handleExitDemo} />}

        {/* Guided Tour */}
        <GuidedTour
          isOpen={showGuidedTour}
          onClose={() => setShowGuidedTour(false)}
          onNavigate={setCurrentPage}
          currentPage={currentPage}
        />

        {/* Sync Status Indicators */}
        {syncStatus === 'saving' && navigator.onLine && (
          <div className="fixed bottom-20 md:bottom-4 right-4 z-50 flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-full shadow-lg">
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Saving...
          </div>
        )}

        {syncStatus === 'error' && (
          <div className="fixed bottom-20 md:bottom-4 right-4 z-50 flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-full shadow-lg">
            <span>⚠️</span>
            Sync error - retrying...
          </div>
        )}

        {/* Page Content */}
        <div className="fixed top-16 left-0 right-0 bottom-16 md:bottom-0 overflow-y-auto overflow-x-hidden bg-gray-50 dark:bg-gray-900" {...swipeHandlers}>
          {currentPage === 'dashboard' ? (
            <Dashboard
              summary={summary}
              upcomingShows={getUpcomingShows()}
              shows={shows}
              totalInventoryValue={getTotalInventoryValue()}
              totalDocuments={documents.length}
              onNavigate={setCurrentPage}
              loading={!dataLoaded && !isDemoMode}
              isEditing={dashboardEditing}
              setIsEditing={setDashboardEditing}
              setFeedbackModalOpen={setFeedbackModalOpen}
              isAdmin={isDemoMode ? true : isAdmin}
              highlightUpcomingShows={highlightUpcomingShows}
              onClearHighlight={() => setHighlightUpcomingShows(false)}
            />
          ) : currentPage === 'summary' ? (
            (isDemoMode || isAdmin) ? (
              <Summary
                summary={summary}
                onNavigate={setCurrentPage}
                onExportShows={handleExportShows}
                onExportExpenses={handleExportExpenses}
                onExportInventory={handleExportInventory}
                onExportAll={handleExportAll}
                generalExpenses={generalExpenses} 
                loading={!dataLoaded && !isDemoMode}
                onUpdateShow={updateShow}
                documents={documents}  
                salesLog={salesLog} 
              />
            ) : (
              <AdminOnlyView onBack={() => setCurrentPage('dashboard')} title="Financial summaries" />
            )
          ) : currentPage === 'expenses' ? (
            (isDemoMode || isAdmin) ? (
              <Expenses
                generalExpenses={generalExpenses}
                onNavigate={setCurrentPage}
                onAddExpense={addGeneralExpense}
                onUpdateExpense={updateGeneralExpense}
                onDeleteExpense={deleteGeneralExpense}
                loading={!dataLoaded && !isDemoMode}
              />
            ) : (
              <AdminOnlyView onBack={() => setCurrentPage('dashboard')} title="Expenses" />
            )
          ) : currentPage === 'timeline' ? (
            <Timeline
              shows={shows}
              onNavigate={setCurrentPage}
              onDeleteShow={deleteShow}
              onUpdateShow={updateShow}
              loading={!dataLoaded && !isDemoMode}
              salesLog={salesLog}
              documents={documents} 
            />
          ) : currentPage === 'inventory' ? (
            <Inventory
              inventory={inventory}
              currency={userCurrency}
              onCurrencyChange={handleCurrencyChange}
              shows={shows}
              onNavigate={setCurrentPage}
              onAddItem={addInventoryItem}
              onUpdateItem={updateInventoryItem}
              onDeleteItem={deleteInventoryItem}
              onRecordSale={handleRecordMerchSale}
              salesLog={salesLog}
              onAddSaleLog={handleAddSaleLog}
              onUpdateShow={updateShow}
              loading={!dataLoaded && !isDemoMode}
              isAdmin={isDemoMode ? true : isAdmin}
            />
          ) : currentPage === 'calendar' ? (
            <Calendar
              shows={shows}
              onNavigate={setCurrentPage}
              onAddShow={addShow}
              onUpdateShow={updateShow}
              onDeleteShow={deleteShow}
              onToggleShow={toggleShow}
              documents={documents} 
              onAddDocument={handleAddDocument}
              salesLog={salesLog}   
              loading={!dataLoaded && !isDemoMode}
              pendingShowToEdit={pendingShowToEdit}
              onClearPendingShow={() => setPendingShowToEdit(null)}
            />
          ) : currentPage === 'documents' ? (
            <Documents
              documents={documents}
              shows={getAllShowsData()}
              onNavigate={setCurrentPage}
              onAddDocument={handleAddDocument}
              onUpdateDocument={handleUpdateDocument}
              onDeleteDocument={handleDeleteDocument}
            />
          ) : currentPage === 'contacts' ? (
            (isDemoMode || isAdmin) ? (
              <Contacts
                shows={shows}
                contacts={contacts}
                onNavigate={setCurrentPage}
                onAddContact={addContact}
                onUpdateContact={updateContact}
                onDeleteContact={deleteContact}
                loading={!dataLoaded && !isDemoMode}
                onUpdateShow={updateShow}
              />
            ) : (
              <AdminOnlyView onBack={() => setCurrentPage('dashboard')} title="Contacts" />
            )
          ) : currentPage === 'polls' ? (
            <Polls
              polls={polls}
              currentUserId={user?.uid}
              isAdmin={isDemoMode ? true : isAdmin}
              members={getGroupMembers()}
              upcomingShows={getUpcomingShows()}
              onAddPoll={handleAddPoll}
              onVote={handleVotePoll}
              onClosePoll={handleClosePoll}
              onDeletePoll={handleDeletePoll}
              onNavigate={setCurrentPage}
              onDeleteShow={(showInfo, pollId) => {
                if (showInfo.dateKey && showInfo.showId) {
                  deleteShow(showInfo.dateKey, showInfo.showId);
                  if (pollId) {
                    handleDeletePoll(pollId);
                  }
                  toast.success('Show removed from calendar');
                }
              }}
              onConfirmShow={(showInfo, pollId) => {
                if (showInfo.dateKey && showInfo.showId) {
                  updateShow(showInfo.dateKey, showInfo.showId, { status: 'confirmed' });
                  if (pollId) {
                    handleDeletePoll(pollId);
                  }
                  toast.success('Show confirmed!');
                }
              }}
            />
          ) : currentPage === 'group-settings' ? (
            isDemoMode ? (
              <DemoModeSettingsView onCreateBand={handleExitDemo} />
            ) : (
              <GroupSettings onNavigate={setCurrentPage} />
            )
          ) : currentPage === 'join-group' ? (
            <JoinGroupManual
              user={user}
              onSuccess={() => {
                setCurrentPage('dashboard');
                toast.success('Successfully joined the band!');
              }}
              onCancel={() => setCurrentPage('dashboard')}
            />
          ) : currentPage === 'feedback-admin' ? (
            <FeedbackAdmin onNavigate={setCurrentPage} />
          ) : null}
        </div>

        {/* Modals */}
        <BandsintownImport
          isOpen={bandsintownModalOpen}
          onClose={() => setBandsintownModalOpen(false)}
          onImportShows={handleBandsintownImport}
          existingShows={shows}
        />

        <FeedbackModal
          isOpen={feedbackModalOpen}
          onClose={() => setFeedbackModalOpen(false)}
          user={user}
          groupName={currentGroup?.name}
        />

        <EditProfileModal
          isOpen={editProfileModalOpen}
          onClose={() => setEditProfileModalOpen(false)}
          user={user}
          currentGroup={currentGroup}
          onUpdateMemberName={updateMemberName}
        />

        <NotificationSettingsModal
          isOpen={notificationSettingsOpen}
          onClose={() => setNotificationSettingsOpen(false)}
          user={user}
          currentGroup={currentGroup}
        />

        {/* Bottom Navigation */}
        <BottomNav
          currentPage={currentPage}
          onNavigate={setCurrentPage}
          isAdmin={isDemoMode ? true : isAdmin}
          unvotedPollsCount={getUnvotedPollsCount()}
        />

        <PWAInstallPrompt user={user} />
        <EmailUpdatesBanner />
        <OfflineIndicator />
      </>
    </SubscriptionProvider>
  );
}

// =====================================================
// HELPER COMPONENTS
// =====================================================
const AdminOnlyView = ({ onBack, title }) => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md text-center">
      <div className="text-6xl mb-4">🔒</div>
      <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">Admin Only</h2>
      <p className="text-gray-600 dark:text-gray-400 mb-6">{title} are only visible to band admins.</p>
      <button
        onClick={onBack}
        className="px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
      >
        Back to Dashboard
      </button>
    </div>
  </div>
);

const DemoModeSettingsView = ({ onCreateBand }) => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md text-center">
      <div className="text-6xl mb-4">🎸</div>
      <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">Demo Mode</h2>
      <p className="text-gray-600 dark:text-gray-400 mb-6">
        Create your own band to access settings and invite members.
      </p>
      <button
        onClick={onCreateBand}
        className="px-6 py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition-colors"
      >
        Create My Band
      </button>
    </div>
  </div>
);

export default App;